import { FC, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTypedSelector } from '../hooks/useTypedSelector'
import { ANALYTICS_CONSENT_KEY, AnalyticsConsent, applyAnalyticsConsent, readAnalyticsConsent, saveAnalyticsConsent } from '../models/analyticsConsent'
import styles from './css/CookieConsent.module.css'

interface Props {
	open: boolean
	onClose: () => void
}

const CookieConsent: FC<Props> = ({ open, onClose }) => {
	const russian = useTypedSelector(state => state.lang.current) === 0
	const [consent, setConsent] = useState(readAnalyticsConsent)
	const rejectButton = useRef<HTMLButtonElement>(null)
	const visible = open || consent === null

	useEffect(() => {
		applyAnalyticsConsent(readAnalyticsConsent())
		const sync = (event: StorageEvent) => {
			if (event.key !== null && event.key !== ANALYTICS_CONSENT_KEY) return
			const value = readAnalyticsConsent()
			applyAnalyticsConsent(value)
			setConsent(value)
		}
		window.addEventListener('storage', sync)
		return () => window.removeEventListener('storage', sync)
	}, [])

	useEffect(() => {
		if (open) rejectButton.current?.focus()
	}, [open])

	const choose = (value: AnalyticsConsent) => {
		saveAnalyticsConsent(value)
		setConsent(value)
		onClose()
	}

	if (!visible) return null

	return (
		<section className={styles.panel} aria-labelledby='cookie-consent-title'>
			<div className={styles.heading}>
				<h2 id='cookie-consent-title'>{russian ? 'Использование файлов cookie' : 'Use of cookies'}</h2>
				{consent !== null && <button type='button' className={styles.close} onClick={onClose} aria-label={russian ? 'Закрыть настройки' : 'Close settings'}>×</button>}
			</div>
			<p>{russian
				? 'Технические файлы cookie необходимы для авторизации и сохранения настроек. Google Analytics собирает статистику посещений только с вашего согласия. Отказ от аналитики не ограничивает доступ к сервису.'
				: 'Essential cookies support authentication and preferences. Google Analytics collects website usage statistics only with your consent. Declining analytics does not restrict access to the service.'}</p>
			<p className={styles.details}><Link to='/privacy'>{russian ? 'Политика конфиденциальности' : 'Privacy policy'}</Link>. {russian ? 'Выбор можно изменить в разделе «Настройки cookies» внизу страницы.' : 'Your selection can be changed in Cookie settings at the bottom of the page.'}</p>
			{consent !== null && <p className={styles.status}>{russian
				? `Аналитические файлы cookie: ${consent === 'granted' ? 'разрешены' : 'отключены'}.`
				: `Analytics cookies: ${consent === 'granted' ? 'enabled' : 'disabled'}.`}</p>}
			<div className={styles.actions}>
				<button ref={rejectButton} type='button' className={`${styles.button} ${styles.secondary}`} aria-label={russian ? 'Отклонить аналитические файлы cookie' : 'Decline analytics cookies'} onClick={() => choose('denied')}>{russian ? 'Отклонить' : 'Decline'}</button>
				<button type='button' className={`${styles.button} ${styles.primary}`} aria-label={russian ? 'Принять аналитические файлы cookie' : 'Accept analytics cookies'} onClick={() => choose('granted')}>{russian ? 'Принять' : 'Accept'}</button>
			</div>
		</section>
	)
}

export default CookieConsent
